import { useState, useEffect } from 'react'
import {
	PhoneIcon,
	ChatBubbleLeftRightIcon,
	DocumentTextIcon,
	ChartBarIcon,
	ClipboardDocumentListIcon,
	CheckIcon,
	PlayIcon,
	ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'
import moment from 'moment'
import { io } from 'socket.io-client'
import CustomerService from '@/services/CustomerService'
import { useNotifications } from '@/hooks/useNotifications'
import { useTranslation } from 'react-i18next'

export default function ContactRecord({ customerId, customerDetails, onCustomerNumberUpdate, onSalesRepNumberUpdate }) {
	const { t } = useTranslation()
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [callSummary, setCallSummary] = useState(customerDetails?.callSummary || '')
	const [savingSummary, setSavingSummary] = useState(false)
	const [summaryError, setSummaryError] = useState(null)
	const [summaryMessage, setSummaryMessage] = useState(null)
	const [callingWithAnalysis, setCallingWithAnalysis] = useState(false)
	const [transcript, setTranscript] = useState("")

	useNotifications({
		error: summaryError,
		message: summaryMessage,
		clearError: () => setSummaryError(null),
		clearMessage: () => setSummaryMessage(null),
	})

	// Update call summary when customerDetails changes
	useEffect(() => {
		setCallSummary(customerDetails?.callSummary || '')
	}, [customerDetails])

	// Socket.IO connection
	useEffect(() => {
		const apiUrl = window.RUNTIME_CONFIG?.API_URL || 'http://localhost:5001'
		
		console.log('Connecting to Socket.IO:', apiUrl)
		const socket = io(apiUrl, {
			transports: ['websocket', 'polling'],
			autoConnect: true,
		})

		socket.on('connect', () => {
			console.log('Socket.IO connected:', socket.id)
		})

		socket.on('disconnect', (reason) => {
			console.log('Socket.IO disconnected:', reason)
		})

		socket.on('connect_error', (error) => {
			console.error('Socket.IO connection error:', error)
		})

		// Listen to all events and log them
		socket.onAny((eventName, ...args) => {
			// console.log('Socket.IO event received:', eventName, args)
			if (eventName === 'transcript') {
				console.log('Transcript received:', args[0].transcript)
				let currentTranscript = transcript
				currentTranscript += args[0].transcript
				setTranscript(currentTranscript + '\n\n')
			}
		})

		// Cleanup on unmount
		return () => {
			console.log('Disconnecting Socket.IO')
			socket.disconnect()
		}
	}, [])

	const customer = customerDetails
	// Phone number is synchronized from parent state (updated via onCustomerNumberUpdate)
	// This ensures both CustomerDetailsCard and ContactRecord stay in sync
	const phoneNumber = customer?.tel || ''
	// Sales rep number is synchronized from parent state (updated via onSalesRepNumberUpdate)
	// This ensures both CustomerDetailsCard and ContactRecord stay in sync
	const salesRepNumber = customer?.salesRepNumber || ''
	const whatsappUrl = phoneNumber ? `https://wa.me/${phoneNumber.replace(/[^0-9]/g, '')}` : '#'
	const callUrl = phoneNumber ? `tel:${phoneNumber}` : '#'

	const handleCall = () => {
		if (phoneNumber) {
			window.location.href = callUrl
		}
	}

	const handleWhatsapp = () => {
		if (phoneNumber) {
			window.open(whatsappUrl, '_blank')
		}
	}

	const handleCallWithAnalysis = async () => {
		setCallingWithAnalysis(true);
		const { data, error } = await CustomerService.callWithAnalysis(phoneNumber, salesRepNumber)
		console.log(`Data, error: `, data, error)
		setCallingWithAnalysis(false)
		if (salesRepNumber) {
			onSalesRepNumberUpdate(salesRepNumber)
		}
		setSummaryError(error || null)
	}

	const handleSaveSummary = async () => {
		if (!customerId || !customerDetails?._id) return

		setSavingSummary(true)
		setSummaryError(null)
		setSummaryMessage(null)

		try {
			const { data, error: saveError } = await CustomerService.updateCustomer(customerId, {
				callSummary: callSummary,
			})

			if (saveError) {
				setSummaryError(saveError)
			} else {
				setSummaryMessage(t('customers.contactRecord.summary.savedSuccess'))
			}
		} catch (err) {
			setSummaryError(err.message || t('customers.contactRecord.summary.saveFailed'))
		} finally {
			setSavingSummary(false)
		}
	}
	
	return (
		<div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
			{/* Actions Card */}
			<div className="col-span-1 xl:col-span-2">
				<div className="rounded-md border border-slate-200 bg-white shadow-sm">
					{/* Header */}
					<div className="border-b border-slate-300 bg-gray-100 px-6 py-3">
						<h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
							<PhoneIcon className="h-5 w-5 text-sky-700" />
							<span>{t('customers.contactRecord.actions.title')}</span>
						</h3>
					</div>

					{/* Actions */}
					<div className="px-6 py-6">
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
							<button
								type="button"
								onClick={handleCall}
								disabled={!phoneNumber}
								className={`inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-md shadow-sm transition ${
									phoneNumber
										? 'bg-sky-600 text-white hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2'
										: 'bg-gray-300 text-gray-500 cursor-not-allowed'
								}`}
							>
								<PhoneIcon className="h-5 w-5" />
								<span>{t('customers.contactRecord.actions.call')}</span>
							</button>

							<button
								type="button"
								onClick={handleCallWithAnalysis}
								disabled={!phoneNumber || callingWithAnalysis}
								className={`inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-md shadow-sm transition ${
									phoneNumber
										? 'bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
										: 'bg-gray-300 text-gray-500 cursor-not-allowed'
								}`}
							>
								<ChartBarIcon className="h-5 w-5" />
								<span>{callingWithAnalysis ? t('customers.contactRecord.actions.calling') : t('customers.contactRecord.actions.callWithAnalysis')}</span>
							</button>

							<button
								type="button"
								onClick={handleWhatsapp}
								disabled={!phoneNumber}
								className={`inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-md shadow-sm transition ${
									phoneNumber
										? 'bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
										: 'bg-gray-300 text-gray-500 cursor-not-allowed'
								}`}
							>
								<ChatBubbleLeftRightIcon className="h-5 w-5" />
								<span>{t('customers.contactRecord.actions.whatsapp')}</span>
							</button>
						</div>
					</div>
				</div>
			</div>

			{/* Call Summary Card */}
			<div className="col-span-1 xl:col-span-2">
				<div className="rounded-md border border-slate-200 bg-white shadow-sm">
					{/* Header */}
					<div className="border-b border-slate-300 bg-gray-100 px-6 py-3">
						<h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
							<ClipboardDocumentListIcon className="h-5 w-5 text-sky-700" />
							<span>{t('customers.contactRecord.summary.title')}</span>
						</h3>
					</div>

					{/* Content */}
					<div className="px-6 py-4">
						<div className="space-y-3">
							<textarea
								value={callSummary}
								onChange={(e) => setCallSummary(e.target.value)}
								placeholder={t('customers.contactRecord.summary.placeholder')}
								disabled={savingSummary}
								rows={6}
								className="block w-full resize-none rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
							/>
							<div className="flex items-center gap-2 justify-end">
								<button
									type="button"
									onClick={handleSaveSummary}
									disabled={savingSummary}
									className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md shadow-sm hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
								>
									<CheckIcon className="h-4 w-4" />
									{savingSummary ? t('customers.contactRecord.summary.saving') : t('customers.contactRecord.summary.save')}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Transcript Card */}
			<div className="col-span-1">
				<div className="rounded-md border border-slate-200 bg-white shadow-sm h-full flex flex-col">
					{/* Header */}
					<div className="border-b border-slate-300 bg-gray-100 px-6 py-3">
						<h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
							<DocumentTextIcon className="h-5 w-5 text-sky-700" />
							<span>{t('customers.contactRecord.transcript.title')}</span>
						</h3>
					</div>

					{/* Content */}
					<div className="flex-1 px-6 py-4 overflow-y-auto">
						{loading ? (
							<div className="text-sm text-gray-500">{t('customers.contactRecord.transcript.loading')}</div>
						) : error ? (
							<div className="text-sm text-red-600">{error}</div>
						) : (
							<div className="space-y-4">
								{/* Placeholder for transcript content */}
								<div className="text-sm text-gray-600">
									<p className="text-gray-700 whitespace-pre-wrap">
										{transcript}
									</p>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Analysis Card */}
			<div className="col-span-1">
				<div className="rounded-md border border-slate-200 bg-white shadow-sm h-full flex flex-col">
					{/* Header */}
					<div className="border-b border-slate-300 bg-gray-100 px-6 py-3">
						<h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
							<ChartBarIcon className="h-5 w-5 text-sky-700" />
							<span>{t('customers.contactRecord.analysis.title')}</span>
						</h3>
					</div>

					{/* Content */}
					<div className="flex-1 px-6 py-4 overflow-y-auto">
						{loading ? (
							<div className="text-sm text-gray-500">{t('customers.contactRecord.analysis.loading')}</div>
						) : error ? (
							<div className="text-sm text-red-600">{error}</div>
						) : (
							<div className="space-y-4">
								{/* Placeholder for analysis content */}
								<div className="text-sm text-gray-600">
									<p className="mb-2">
										<span className="font-semibold text-gray-900">{t('customers.contactRecord.analysis.lastAnalyzed')}</span>{' '}
										{moment().format('YYYY-MM-DD HH:mm')}
									</p>
									<div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
										<p className="text-gray-700 whitespace-pre-wrap">
											{t('customers.contactRecord.analysis.empty')}
										</p>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Recording Card */}
			<div className="col-span-1">
				<div className="rounded-md border border-slate-200 bg-white shadow-sm h-full flex flex-col">
					{/* Header */}
					<div className="border-b border-slate-300 bg-gray-100 px-6 py-3">
						<h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
							<PlayIcon className="h-5 w-5 text-sky-700" />
							<span>{t('customers.contactRecord.recording.title')}</span>
						</h3>
					</div>

					{/* Content */}
					<div className="flex-1 px-6 py-4 overflow-y-auto">
						<div className="space-y-4">
							<div className="text-sm text-gray-600">
								<p className="mb-4 text-gray-700">
									{t('customers.contactRecord.recording.description')}
								</p>
							</div>
							<button
								type="button"
								disabled
								className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md shadow-sm transition bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 opacity-75 cursor-not-allowed"
							>
								<ArrowDownTrayIcon className="h-4 w-4" />
								{t('customers.contactRecord.recording.download')}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
